/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import useLoadingDots from "@/shared/hooks/useLoadingDots";
import { Typography } from "@mui/material";

import { Badge, BadgeColors, colors } from "@dotnet/telematics-design-system";

import { SCHEDULE_CARDS_STATUSES } from "../../types/enums";
import SingleRow from "./SingleRow";

interface SingleCardProps {
  cardStatus: SCHEDULE_CARDS_STATUSES;
  cardTitle: string;
  dueDate: string | null | undefined;
  badgeText: string;
  failReason?: string | null;
  nextAttempt?: string;
  currentStage?: string | null;
}

const SingleCard = ({
  cardStatus,
  cardTitle,
  dueDate,
  badgeText,
  failReason,
  nextAttempt,
  currentStage
}: SingleCardProps) => {
  const { t } = useTranslation();

  const dots = useLoadingDots();

  const borderColors: Record<SCHEDULE_CARDS_STATUSES, string> = {
    [SCHEDULE_CARDS_STATUSES.AwaitingConnection]: colors.blue500,
    [SCHEDULE_CARDS_STATUSES.Blocked]: colors.red300,
    [SCHEDULE_CARDS_STATUSES.Failed]: colors.red300,
    [SCHEDULE_CARDS_STATUSES.InProgress]: colors.blue500,
    [SCHEDULE_CARDS_STATUSES.Neutral]: colors.gray200,
    [SCHEDULE_CARDS_STATUSES.RequirementNotMet]: colors.orange300,
    [SCHEDULE_CARDS_STATUSES.Successful]: colors.green300
  };

  const backgroundColors: Record<SCHEDULE_CARDS_STATUSES, string> = {
    [SCHEDULE_CARDS_STATUSES.AwaitingConnection]: colors.blue100,
    [SCHEDULE_CARDS_STATUSES.Blocked]: colors.red50,
    [SCHEDULE_CARDS_STATUSES.Failed]: colors.red50,
    [SCHEDULE_CARDS_STATUSES.InProgress]: colors.blue100,
    [SCHEDULE_CARDS_STATUSES.Neutral]: colors.white,
    [SCHEDULE_CARDS_STATUSES.RequirementNotMet]: colors.orange50,
    [SCHEDULE_CARDS_STATUSES.Successful]: colors.green50
  };

  const badgeColors: Record<SCHEDULE_CARDS_STATUSES, BadgeColors> = {
    [SCHEDULE_CARDS_STATUSES.AwaitingConnection]: BadgeColors.info,
    [SCHEDULE_CARDS_STATUSES.Blocked]: BadgeColors.critical,
    [SCHEDULE_CARDS_STATUSES.Failed]: BadgeColors.critical,
    [SCHEDULE_CARDS_STATUSES.InProgress]: BadgeColors.info,
    [SCHEDULE_CARDS_STATUSES.Neutral]: BadgeColors.light,
    [SCHEDULE_CARDS_STATUSES.RequirementNotMet]: BadgeColors.warning,
    [SCHEDULE_CARDS_STATUSES.Successful]: BadgeColors.success
  };

  const backgroundColor = backgroundColors[cardStatus];
  const borderColor = borderColors[cardStatus];
  const badgeColor = badgeColors[cardStatus];
  const isInverted = cardStatus !== SCHEDULE_CARDS_STATUSES.Neutral;

  const dateText =
    cardStatus === SCHEDULE_CARDS_STATUSES.Successful
      ? t("Schedules##next due date")
      : t("Schedules##due date");

  return (
    <div
      css={css({
        padding: "8px 12px 12px 12px",
        borderRadius: "8px",
        border: `1px solid ${borderColor}`,
        backgroundColor,
        display: "flex",
        gap: "6px",
        flexDirection: "column"
      })}
    >
      <div
        css={css({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        })}
      >
        <Typography variant="body3Medium">{cardTitle}</Typography>
        <Badge color={badgeColor} inverted={isInverted}>
          <Typography variant="body3Medium">{badgeText}</Typography>
        </Badge>
      </div>

      {currentStage && (
        <Typography
          sx={{
            color: colors.textSecondary,
            position: "relative",
            display: "inline-block"
          }}
          variant="body3Regular"
        >
          {t(`Download monitoring##Stages##${currentStage}`)}
          <span css={css({ position: "absolute" })}>{dots}</span>
        </Typography>
      )}

      {failReason && (
        <SingleRow
          textLeft={t("Schedules##fail reason")}
          textRight={failReason}
        />
      )}

      {nextAttempt && (
        <SingleRow
          textLeft={t("Schedules##next attempt")}
          textRight={nextAttempt}
        />
      )}

      {dueDate && <SingleRow textLeft={dateText} textRight={dueDate} />}
    </div>
  );
};

export default SingleCard;
